import { imageAccessAPI } from './api';
import { isManagedStorageUrl, toAbsoluteViewPath } from './imageDisplayAccess';
import { rebaseUploadsUrl, uploadsPathFromSignedViewUrl } from './imageUrlUtils';

/**
 * 与后端默认 `ipe.storage.image-access.external-ttl-seconds`（1800）对齐。
 * 无 Expires 的外链按此保守寿命缓存，避免超长导出复用已过期预签名。
 */
export const EXPORT_EXTERNAL_CACHE_FALLBACK_TTL_MS = 20 * 60 * 1000;

/** 距 OSS Expires 少于此余量则视为缓存失效，强制重新签名 */
export const EXPORT_EXTERNAL_CACHE_REFRESH_LEAD_MS = 5 * 60 * 1000;

/** 导出外链缓存：同一次导出内复用尚未临近过期的 OSS 预签名结果 */
const exportExternalUrlCache = new Map();

export function clearExportExternalUrlCache() {
  exportExternalUrlCache.clear();
}

/** 解析 OSS 预签名 URL 的绝对过期时刻（ms）；无法解析则返回 0 */
export function parseExportExternalExpiresAtMs(url) {
  const s = String(url || '').trim();
  if (!s.startsWith('http://') && !s.startsWith('https://')) return 0;
  try {
    const parsed = new URL(s);
    const expires = Number(parsed.searchParams.get('Expires') || parsed.searchParams.get('expires'));
    if (!Number.isFinite(expires) || expires <= 0) return 0;
    // OSS 一般为 Unix epoch 秒；兼容误传毫秒
    return expires > 1_000_000_000_000 ? expires : expires * 1000;
  } catch {
    return 0;
  }
}

function resolveCacheUsableUntilMs(externalUrl, nowMs = Date.now()) {
  const expiresAtMs = parseExportExternalExpiresAtMs(externalUrl);
  if (expiresAtMs > 0) {
    return expiresAtMs - EXPORT_EXTERNAL_CACHE_REFRESH_LEAD_MS;
  }
  return nowMs + EXPORT_EXTERNAL_CACHE_FALLBACK_TTL_MS;
}

function readCachedExternal(canonical, nowMs = Date.now()) {
  const entry = exportExternalUrlCache.get(canonical);
  if (!entry?.url) return '';
  if (nowMs >= entry.usableUntilMs) {
    exportExternalUrlCache.delete(canonical);
    return '';
  }
  return entry.url;
}

function writeCachedExternal(canonical, external, nowMs = Date.now()) {
  exportExternalUrlCache.set(canonical, {
    url: external,
    usableUntilMs: resolveCacheUsableUntilMs(external, nowMs),
  });
}

/** 外网平台无法拉取的主机（本机 / 内网 / link-local） */
export function isNonRoutableExportHost(hostname) {
  const h = String(hostname || '').toLowerCase().replace(/^\[|\]$/g, '');
  if (!h) return true;
  if (h === 'localhost' || h === '::1' || h.endsWith('.localhost') || h.endsWith('.local')) {
    return true;
  }
  if (/^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^169\.254\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  return false;
}

/**
 * sign-batch-external 产出 / 第三方公网直链是否可作为上架平台外链。
 * 拒绝站内 /view、本机与内网地址；OSS 预签名即使 path 含 image-space/ 也应放行。
 */
export function isPlatformExportableExternalUrl(url) {
  const s = String(url || '').trim();
  if (!s.startsWith('http://') && !s.startsWith('https://')) return false;
  if (s.includes('/api/storage/images/view')) return false;
  try {
    if (isNonRoutableExportHost(new URL(s).hostname)) return false;
  } catch {
    return false;
  }
  return true;
}

/**
 * 已是可直接写入 Excel 的公网直链（非受管、非 view、非内网）。
 * 受管地址必须走换签，不得靠本函数提前透传。
 */
export function isAlreadyPublicExportUrl(url) {
  if (!isPlatformExportableExternalUrl(url)) return false;
  if (isManagedStorageUrl(url)) return false;
  return true;
}

export function resolveCanonicalManagedUrlForExport(url) {
  const s = String(url || '').trim();
  if (!s) return '';
  const fromView = uploadsPathFromSignedViewUrl(s);
  if (fromView) return fromView;
  const rebased = rebaseUploadsUrl(s);
  if (rebased) {
    if (rebased.startsWith('/uploads/') && isManagedStorageUrl(rebased)) return rebased;
    if (rebased.startsWith('http://') || rebased.startsWith('https://')) {
      try {
        const path = new URL(rebased).pathname;
        if (path.startsWith('/uploads/') && isManagedStorageUrl(path)) return path;
      } catch {
        /* ignore */
      }
    }
  }
  if (s.startsWith('/uploads/') && isManagedStorageUrl(s)) return s;
  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const parsed = new URL(s);
      if (parsed.pathname.startsWith('/uploads/') && isManagedStorageUrl(parsed.pathname)) {
        return parsed.pathname;
      }
      const key = parsed.pathname.replace(/^\//, '');
      const asUploads = key ? `/uploads/${key}` : '';
      if (asUploads && isManagedStorageUrl(asUploads)) return asUploads;
    } catch {
      /* ignore */
    }
  }
  if (isManagedStorageUrl(s)) return s;
  return '';
}

export async function resolveExportExternalPublicUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (raw.startsWith('blob:') || raw.startsWith('data:')) return raw;
  if (isAlreadyPublicExportUrl(raw)) return raw;

  const canonical = resolveCanonicalManagedUrlForExport(raw);
  if (!canonical) return '';

  const cached = readCachedExternal(canonical);
  if (cached) return cached;

  try {
    const res = await imageAccessAPI.signBatchExternal([canonical]);
    const map = res?.urls && typeof res.urls === 'object' ? res.urls : {};
    // 严格按请求 key 取值；禁止 Object.values(map)[0] 回退，避免 key 对不上时误用他人预签名
    const external = map[canonical] || '';
    // 拒绝 local 绝对 /view：startsWith(http) 不够，外网平台仍拉不到
    if (isPlatformExportableExternalUrl(external)) {
      writeCachedExternal(canonical, external);
      return external;
    }
  } catch (error) {
    console.warn('[导出] 换取 OSS 外链失败', canonical, error);
  }
  return '';
}

/** 导出到 Excel 的图片链接：受管存储走 OSS 预签名直链，平台可外链访问 */
export async function toExportPublicUrl(url) {
  if (!url) return '';
  const s = String(url).trim();
  if (s.startsWith('blob:') || s.startsWith('data:')) return s;
  if (isAlreadyPublicExportUrl(s)) return s;

  const external = await resolveExportExternalPublicUrl(s);
  if (external) return external;

  // 受管 https 换签失败后不再原样透传，避免把私有/不可外链地址写进 Excel
  if (s.startsWith('http://') || s.startsWith('https://')) {
    return '';
  }

  const canonical = resolveCanonicalManagedUrlForExport(s);
  if (canonical) return '';

  const rebased = rebaseUploadsUrl(s);
  if (rebased && !isManagedStorageUrl(rebased)) {
    // 仅当 rebase 结果本身已是公网直链时放行；不拼站内绝对路径冒充外链
    if (isAlreadyPublicExportUrl(rebased)) return rebased;
    if (rebased.startsWith('http://') || rebased.startsWith('https://')) return '';
    const abs = toAbsoluteViewPath(rebased);
    return isAlreadyPublicExportUrl(abs) ? abs : '';
  }
  return '';
}
